import React, { memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Circle, Clock, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Z_CLASS } from '../../constants/zIndex';
import { NotificationCenter } from '../ui/NotificationCenter';

/**
 * TopStatusBar - Minimal status bar with service indicators
 * Clean, professional design without distracting animations
 */
const TopStatusBar = memo(function TopStatusBar({
  activeTask,
  nextTask,
  timeLeft,
  progressPercent,
  dailyProgress,
  servicesStatus,    // { groq, calendar, firebase, google }
  onStatusClick,     // Callback when clicking status icons
  onCrisisClick,     // Callback for crisis mode
  onReconnect,       // Callback pour reconnecter le token Google expiré
  conflictCount = 0, // Number of unresolved sync conflicts
  onConflictsClick,  // Opens ConflictResolutionSheet
  pendingSyncOps = 0,// Number of operations queued for sync
  isSyncing = false, // Show shimmer progress bar
  // Notification center
  notifHistory = [],
  unreadNotifCount = 0,
  onMarkAllRead,
  onMarkRead,
  onClearNotifHistory,
}) {
  const hasActiveTask = !!activeTask;
  const hasNextTask = !!nextTask;

  return (
    <motion.header
      initial={{ y: -52, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 ${Z_CLASS.TOP_STATUS_BAR} h-[52px] bg-[var(--bg-surface,#0c0c0f)] border-b border-[var(--border-subtle,rgba(255,255,255,0.06))]`}
    >
      {/* Shimmer sync progress bar (2px, bottom of header) */}
      <AnimatePresence>
        {isSyncing && (
          <motion.div
            className="absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="h-full w-1/3 bg-gradient-to-r from-transparent via-cyan-400 to-transparent"
              animate={{ x: ['calc(-100%)', 'calc(400%)'] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-md mx-auto h-full px-4 flex items-center justify-between">
        {/* Services Status Indicators - Left side */}
        {servicesStatus && (
          <div className="flex items-center gap-1.5 mr-3">
            <div
              className="flex items-center gap-1.5 cursor-pointer"
              onClick={onStatusClick}
              title="Statut des services"
            >
              <ServiceDot service="groq" status={servicesStatus.groq?.status} />
              <ServiceDot service="calendar" status={servicesStatus.calendar?.status} />
              <ServiceDot service="firebase" status={servicesStatus.firebase?.status} />
              <GoogleTokenDot
                status={servicesStatus.google?.status}
                message={servicesStatus.google?.message}
                onReconnect={onReconnect}
              />
            </div>

            {/* Offline queue counter */}
            {pendingSyncOps > 0 && (
              <div
                className="flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-500/20 rounded-md cursor-default"
                title={`${pendingSyncOps} opération(s) en attente de synchronisation`}
              >
                <span className="text-[12px] font-bold text-amber-400 tabular-nums">
                  {pendingSyncOps}
                </span>
                <span className="text-[12px] text-amber-500">↑</span>
              </div>
            )}

            {/* Conflict badge */}
            {conflictCount > 0 && (
              <button
                onClick={onConflictsClick}
                className="flex items-center gap-1 px-1.5 py-0.5 bg-red-500/20 hover:bg-red-500/30
                  border border-red-500/40 rounded-md transition-colors"
                title={`${conflictCount} conflit(s) de synchronisation`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                <span className="text-[12px] font-bold text-red-400 tabular-nums">
                  {conflictCount}
                </span>
              </button>
            )}
          </div>
        )}

        <AnimatePresence mode="wait">
          {hasActiveTask ? (
            <ActiveTaskDisplay
              key="active"
              task={activeTask}
              timeLeft={timeLeft}
              progressPercent={progressPercent}
            />
          ) : hasNextTask ? (
            <NextTaskDisplay key="next" task={nextTask} />
          ) : (
            <DayProgressDisplay key="progress" dailyProgress={dailyProgress} />
          )}
        </AnimatePresence>

        {/* Notification Center */}
        <div className="ml-2 flex-shrink-0">
          <NotificationCenter
            notifHistory={notifHistory}
            unreadCount={unreadNotifCount}
            onMarkAllRead={onMarkAllRead}
            onMarkRead={onMarkRead}
            onClearHistory={onClearNotifHistory}
          />
        </div>

        {/* Crisis Mode Button - Always visible */}
        {onCrisisClick && (
          <button
            onClick={onCrisisClick}
            className="ml-1 p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-all"
            title="Mode Crise - Aide immédiate"
          >
            <AlertTriangle className="w-4 h-4" />
          </button>
        )}
      </div>
    </motion.header>
  );
});

/**
 * Service status dot indicator
 */
const ServiceDot = memo(function ServiceDot({ service, status }) {
  const icons = {
    groq: '⚡',
    calendar: '📅',
    firebase: '🔥'
  };

  const statusStyles = {
    idle: 'opacity-30 grayscale',
    loading: 'animate-pulse',
    connected: '',
    error: 'grayscale brightness-75'
  };

  // Small colored indicator dot
  const dotColors = {
    idle: 'bg-zinc-500',
    loading: 'bg-amber-400',
    connected: 'bg-emerald-400',
    error: 'bg-red-500'
  };

  return (
    <div
      className={`relative ${statusStyles[status] || statusStyles.idle}`}
      title={`${service}: ${status || 'idle'}`}
    >
      <span className="text-xs">{icons[service]}</span>
      <span
        className={`absolute -bottom-0.5 -right-0.5 w-1.5 h-1.5 rounded-full ${dotColors[status] || dotColors.idle} ${status === 'loading' ? 'animate-ping' : ''}`}
      />
    </div>
  );
});

/**
 * Indicateur de session Google OAuth.
 * Affiche un point discret coloré selon l'état du token.
 * En cas de session expirée, le point devient cliquable pour reconnecter.
 */
const GoogleTokenDot = memo(function GoogleTokenDot({ status, message, onReconnect }) {
  // N'affiche rien si jamais connecté
  if (!status || status === 'idle') return null;

  const dotColors = {
    connected: 'bg-emerald-400',
    loading:   'bg-amber-400 animate-pulse',  // expiring
    error:     'bg-red-500',
    idle:      'bg-zinc-600',
  };

  const isExpired = status === 'error';

  return (
    <div
      className={`relative ${isExpired && onReconnect ? 'cursor-pointer' : ''}`}
      title={message || 'Session Google'}
      onClick={isExpired && onReconnect ? (e) => { e.stopPropagation(); onReconnect(); } : undefined}
    >
      <span className="text-xs">🔑</span>
      <span
        className={`absolute -bottom-0.5 -right-0.5 w-1.5 h-1.5 rounded-full ${dotColors[status] ?? dotColors.idle}`}
      />
    </div>
  );
});

/**
 * Active task - Clean, focused display
 */
const ActiveTaskDisplay = memo(function ActiveTaskDisplay({ task, timeLeft, progressPercent }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="flex items-center gap-3 flex-1 min-w-0"
    >
      {/* Status indicator */}
      <div className="flex items-center gap-2">
        <div className="relative">
          <Circle className="w-2.5 h-2.5 text-emerald-500 fill-emerald-500" />
          <div className="absolute inset-0 w-2.5 h-2.5 bg-emerald-500 rounded-full animate-ping opacity-40" />
        </div>
        <span className="text-[14px] font-medium text-zinc-500 uppercase tracking-wide">
          En cours
        </span>
      </div>

      {/* Task name */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-zinc-100 truncate">
          {task.task}
        </p>
      </div>

      {/* Time remaining */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-zinc-400 tabular-nums">
          {timeLeft}
        </span>
        <div className="w-16 h-1 bg-zinc-800 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-emerald-500 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
      </div>
    </motion.div>
  );
});

/**
 * Next task - Simple preview
 */
const NextTaskDisplay = memo(function NextTaskDisplay({ task }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="flex items-center gap-3 flex-1 min-w-0"
    >
      <div className="flex items-center gap-2">
        <Clock className="w-3.5 h-3.5 text-zinc-500" />
        <span className="text-[14px] font-medium text-zinc-500 uppercase tracking-wide">
          À venir
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-zinc-300 truncate">
          {task.task}
        </p>
      </div>

      <span className="text-sm font-medium text-zinc-500 tabular-nums">
        {task.time}
      </span>
    </motion.div>
  );
});

/**
 * Day progress - Compact stats
 */
const DayProgressDisplay = memo(function DayProgressDisplay({ dailyProgress }) {
  const allDone = dailyProgress.completed === dailyProgress.total && dailyProgress.total > 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="flex items-center gap-3 flex-1 min-w-0"
    >
      <div className="flex items-center gap-2">
        {allDone ? (
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
        ) : (
          <Circle className="w-3.5 h-3.5 text-zinc-600" />
        )}
        <span className="text-[14px] font-medium text-zinc-500 uppercase tracking-wide">
          {allDone ? 'Terminé' : 'Aujourd\'hui'}
        </span>
      </div>

      <div className="flex-1" />

      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-zinc-400">
          {dailyProgress.completed}/{dailyProgress.total}
        </span>
        <div className="w-20 h-1 bg-zinc-800 rounded-full overflow-hidden">
          <motion.div
            className={`h-full rounded-full ${allDone ? 'bg-emerald-500' : 'bg-zinc-600'}`}
            initial={{ width: 0 }}
            animate={{ width: `${dailyProgress.percent}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
      </div>
    </motion.div>
  );
});

export { TopStatusBar };
